/* V947 — image delivery speed helpers.
 *
 * Three small, independent behaviours. None of them touches geometry, framing,
 * Panzoom or the normalizer's contracts; they only decide *when* bytes are
 * fetched.
 *
 *  1. Lightbox warm-up. The PDP gallery link now points at a 2400px render
 *     instead of the untouched master (see sections/main-product.liquid). This
 *     prefetches that render on hover/focus/touch-start, so by the time the
 *     click lands the bytes are usually already in the HTTP cache and the
 *     viewer opens with the image present rather than blank.
 *
 *  2. Card navigation warm-up. V922 added an intent handler, but it only
 *     triggered pixel analysis; it never fetched the destination image. Hovering
 *     a product card now also warms the card raster itself.
 *
 *  3. A hard budget. Prefetching is a bet with the customer's data plan. The
 *     module refuses to place more than a bounded number of bets per page, never
 *     bets at all under Save-Data or 2G, and never bets on the same URL twice.
 */
(function () {
  'use strict';

  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var effectiveType = (connection && connection.effectiveType) || '';
  var saveData = Boolean(connection && connection.saveData);

  // Prefetching helps on a fast link and actively hurts on a metered slow one,
  // where the speculative bytes compete with what the customer is looking at.
  if (saveData || effectiveType === 'slow-2g' || effectiveType === '2g') return;

  var MAX_PREFETCH = effectiveType === '3g' ? 6 : 18;
  var HOVER_DELAY_MS = 90;
  var warmed = new Set();
  var spent = 0;
  var hoverTimer = 0;

  function warm(url) {
    if (!url || warmed.has(url) || spent >= MAX_PREFETCH) return;
    warmed.add(url);
    spent += 1;
    // An <img> beats <link rel=prefetch> here: it lands in the same image cache
    // the viewer will read from, and it is honoured by every browser we target.
    var probe = new Image();
    probe.decoding = 'async';
    if ('fetchPriority' in probe) probe.fetchPriority = 'low';
    probe.src = url;
  }

  function lightboxUrl(anchor) {
    if (!anchor) return '';
    var isPhone = window.matchMedia('(max-width: 760px)').matches;
    var frame = (anchor.getAttribute(isPhone ? 'data-ss-frame-src-mobile' : 'data-ss-frame-src') || '').trim();
    if (frame) return frame;
    if (isPhone) {
      var mobileSrc = (anchor.getAttribute('data-ss-src-mobile') || '').trim();
      if (mobileSrc) return mobileSrc;
    }
    return anchor.getAttribute('href') || '';
  }

  function cardUrl(scope) {
    if (!scope) return '';
    var image = scope.querySelector('img.ss-product-image');
    if (!image) return '';
    return image.currentSrc || image.getAttribute('src') || '';
  }

  function onIntent(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var galleryLink = target.closest('a[data-fancybox]');
    if (galleryLink) {
      warm(lightboxUrl(galleryLink));
      return;
    }

    var productLink = target.closest('a[href*="/products/"]');
    if (!productLink) return;
    var scope = productLink.closest('[data-ss-media-window]') || productLink;
    warm(cardUrl(scope));
  }

  function onHover(event) {
    // Pointerover also fires over every ordinary paragraph, menu and nested
    // element. Avoid creating timers unless the target offers a prefetchable
    // image; still cancel a pending hover when the pointer leaves the link.
    window.clearTimeout(hoverTimer);
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('a[data-fancybox], a[href*="/products/"]')) return;
    hoverTimer = window.setTimeout(function () {
      onIntent(event);
    }, HOVER_DELAY_MS);
  }

  document.addEventListener('pointerover', onHover, { passive: true, capture: true });
  document.addEventListener('pointerdown', onIntent, { passive: true, capture: true });
  document.addEventListener('focusin', onIntent, { capture: true });

  /* When the PDP is idle, warm the gallery slide the customer is most likely to
     open next: the one currently on screen. One image, once. */
  function warmCurrentGallerySlide() {
    var active = document.querySelector(
      '.m6pr a[data-fancybox].is-active, .m6pr li:first-child a[data-fancybox]'
    );
    if (active) warm(lightboxUrl(active));
  }

  function scheduleIdleGalleryWarm() {
    // A speculative 2400px gallery render must not compete with the initial
    // page resources. Schedule only on a product page, after window.load,
    // and skip the optional idle prefetch on 3G (explicit click/hover still
    // prefetches). The original lightbox behaviour is otherwise preserved.
    if (effectiveType === '3g' || !document.querySelector('.m6pr')) return;
    if (document.visibilityState === 'hidden') return;
    function warmIfVisible() {
      if (document.visibilityState !== 'hidden') warmCurrentGallerySlide();
    }
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warmIfVisible, { timeout: 2500 });
    } else {
      window.setTimeout(warmIfVisible, 1200);
    }
  }

  if (document.readyState === 'complete') {
    scheduleIdleGalleryWarm();
  } else {
    window.addEventListener('load', scheduleIdleGalleryWarm, { once: true });
  }
})();
